import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';
import { Typography } from '@material-ui/core';
import { TextField, Button } from '@lavu-pos/lavu-react-components';
import { Spacer, PASSWORD_REGEX, intlShape } from 'modules/utils';
import axios from 'axios';
import { string as yupString, object as yupObject, ref as yupRef } from 'yup';
import { Formik } from 'formik';
import { DECODE_USERNAME_URL, RESET_PASSWORD_URL } from 'utils/constants';
import compose from 'lodash/fp/compose';
import { withAuthContext } from './context';
import { StyledFormMessage, StyledForm, StyledFormHeader } from './styles';
import { loadProtect } from './Protect';

const validationSchema = yupObject().shape({
	password: yupString()
		.required(<FormattedMessage id="loginErrors.empty.password" />)
		.matches(PASSWORD_REGEX, {
			message: <FormattedMessage id="landingResetForm.newPasswordError" />,
			excludeEmptyString: true,
		}),
	passwordRedo: yupString()
		.required(<FormattedMessage id="loginErrors.empty.passwordRedo" />)
		.oneOf(
			[yupRef('password'), null],
			<FormattedMessage id="landingResetForm.passwordDoesntMatch" />
		),
});

class LandingResetForm extends Component {
	static propTypes = {
		userToken: PropTypes.string,
		history: PropTypes.object,
		intl: intlShape,
	};

	static defaultProps = {
		userToken: '',
	};

	constructor(props) {
		super(props);

		const { userToken } = props;

		if (!userToken) {
			this.returnToLogin();
		}

		axios
			.post(DECODE_USERNAME_URL, {
				token: userToken,
			}, { withCredentials: true })
			.then(({ data }) => {
				this.updateUsername(data.username);
			})
			.catch(() => {
				this.returnToLogin();
			});
	}

	state = {
		username: '',
		backEndError: '',
		serverError: false,
		backEndSuccess: false,
	};

	updateUsername = username => {
		this.setState({ username });
	};

	returnToLogin = () => {
		const { history } = this.props;

		history.push('/login');
	};

	onSubmit = async ({ password, passwordRedo }, { setSubmitting }) => {
		const { userToken, intl, history } = this.props;
		const { username } = this.state;
		try {
			await axios.post(
				RESET_PASSWORD_URL,
				{
					username,
					password,
					passwordRedo,
					token: userToken,
				},
				{ withCredentials: true }
			);

			this.setState({
				backEndSuccess: true,
				serverError: false,
				backEndError: '',
			});
		} catch (error) {
			const { response: { data = {} } = {} } = error;
			const { code = 'loginErrors.networkErrorMsg', serverError } = data;

			if (error.response && error.response.headers['x-session']) {
				history.push('/refresh_page?code='+error.response.headers['x-session']);
			}

			this.setState({
				backEndError: intl.formatMessage({
					id: error.response ? `hubqlErrors.${code}` : code,
				}),
				serverError,
			});

			setSubmitting(false);
		}
	};

	componentWillMount()
	{
	  loadProtect();
	}


	render() {
		const { backEndError, backEndSuccess, username, serverError } = this.state;
		const passwordMessage = backEndSuccess && (
			<FormattedMessage id="landingResetForm.newPasswordSuccessSubTitle" />
		);
		return (
			<Formik
				initialValues={{ password: '', passwordRedo: '', username }}
				validationSchema={validationSchema}
				onSubmit={this.onSubmit}
			>
				{({
					values: { password, passwordRedo },
					errors,
					handleSubmit,
					handleChange,
					handleBlur,
					isValid,
					touched,
					isSubmitting,
				}) => (
					<StyledForm>
						<StyledFormHeader>
							<Typography className="login-form-text login-form-title">
								<FormattedMessage id="passwordResetCommon.resetPasswordTitle" />
							</Typography>
						</StyledFormHeader>
						<div>
							<Typography className="login-form-text">
								{passwordMessage}
							</Typography>
						</div>
						{(!!backEndError || serverError) && (
							<StyledFormMessage bgcolor="#de2f55" bdcolor="#a42440">
								<Typography className="password-reset-error">
									{serverError ? (
										<Fragment>
											<FormattedMessage id="landingResetForm.errorResetPassword" />
											<FormattedMessage id="passwordResetEmail.customerCare" />.
										</Fragment>
									) : (
										backEndError
									)}
								</Typography>
							</StyledFormMessage>
						)}
						{backEndSuccess && (
							<Fragment>
								<StyledFormMessage bgcolor="#99bc08" bdcolor="#88a807">
									<Typography className="password-reset-success">
										<FormattedMessage id="landingResetForm.successResetPassword" />
									</Typography>
								</StyledFormMessage>
								<Spacer height={5} />
								<Button kind="success" onClick={this.returnToLogin} fluid>
									<FormattedMessage id="passwordResetForm.returnLogin" />
								</Button>
							</Fragment>
						)}
						{!backEndSuccess && (
							<Fragment>
								<TextField
									type="text"
									name="username"
									label={<FormattedMessage id="commonStrings.currentUser" />}
									autoComplete="username"
									value={username}
									disabled
								/>
								<Spacer height={15} />
								<TextField
									className="login-form-input"
									type="password"
									name="password"
									label={
										<FormattedMessage id="commonStrings.currentPassword" />
									}
									autoComplete="current-password"
									value={password}
									onChange={handleChange}
									onBlur={handleBlur}
									toggle="true"
									error={touched.password && errors.password}
								/>
								<Spacer height={15} />
								<TextField
									className="login-form-input"
									type="password"
									name="passwordRedo"
									label={
										<FormattedMessage id="commonStrings.confirmPassword" />
									}
									autoComplete="new-password"
									value={passwordRedo}
									onChange={handleChange}
									onBlur={handleBlur}
									toggle="true"
									error={touched.passwordRedo && errors.passwordRedo}
								/>
								<Spacer height={15} />
								<Button
									kind="success"
									type="submit"
									isLoading={isSubmitting}
									disabled={!isValid || isSubmitting}
									fluid
									onClick={handleSubmit}
								>
									{isSubmitting ? (
										<FormattedMessage id="loading" />
									) : (
										<FormattedMessage id="passwordResetCommon.submitBtnMsg" />
									)}
								</Button>
							</Fragment>
						)}
					</StyledForm>
				)}
			</Formik>
		);
	}
}

export default compose(
	injectIntl,
	withAuthContext(({ userToken }) => ({
		userToken,
	}))
)(LandingResetForm);
